import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { landing as landingApi, wallet as walletApi } from '@/lib/api';
import PublicNav, { PublicFooter } from '@/components/PublicNav';
import AnalyticsTracker from '@/components/AnalyticsTracker';
import { Check, ArrowRight, Loader2, HelpCircle, Calculator, Brain, Database, Radio, HardDrive, Search, GitBranch, Gauge, Box, Cpu, FileText, Workflow, Clock, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const catMeta = {
  ml_model: { label: 'ML Models', icon: Brain, color: 'bg-violet-100 text-violet-700 border-violet-200' },
  database: { label: 'Databases', icon: Database, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  streaming: { label: 'Streaming', icon: Radio, color: 'bg-orange-100 text-orange-700 border-orange-200' },
  storage: { label: 'Storage', icon: HardDrive, color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  ai_search: { label: 'AI & Search', icon: Search, color: 'bg-pink-100 text-pink-700 border-pink-200' },
};

const svcIcons = { qdrant: Search, elasticsearch: Database, neo4j: GitBranch, redis: Gauge, kafka: Radio, minio: Box, bge_m3: Brain, bge_reranker: Workflow, minilm: Cpu, spacy: FileText };

const tierStyles = {
  economy:  { label: 'Economy',  sub: 'Best value',     bg: 'bg-slate-50',  border: 'border-slate-200', active: 'bg-slate-900 text-white border-slate-900', text: 'text-slate-600' },
  standard: { label: 'Standard', sub: 'Balanced',        bg: 'bg-cyan-50',   border: 'border-cyan-200',  active: 'bg-cyan-600 text-white border-cyan-600',   text: 'text-cyan-700' },
  priority: { label: 'Priority', sub: 'Fastest',         bg: 'bg-amber-50',  border: 'border-amber-200', active: 'bg-amber-500 text-white border-amber-500', text: 'text-amber-700' },
};

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [pricingData, setPricingData] = useState(null);
  // Phase 8 — single account tier governs all services in the calculator
  const [accountTier, setAccountTier] = useState('economy');
  const [selected, setSelected] = useState({});
  const [hoursPerDay, setHoursPerDay] = useState(8);
  const [loading, setLoading] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    landingApi.get().then(r => setContent(r.data)).catch(() => {});
    landingApi.pricing().then(r => setPricingData(r.data)).catch(() => {});
  }, []);

  const services = pricingData?.services || {};
  const footer = content?.footer || {};
  const pricingSection = content?.pricing_section || {};
  const pricingBanner = content?.pricing_banner || {};
  const pricingFaq = content?.pricing_faq || [];
  const welcomeCredit = pricingData?.welcome_credit || {};
  const wcAmount = welcomeCredit.enabled ? (welcomeCredit.amount || 0) : 0;

  // When tier changes, sync every selected service to the new tier
  useEffect(() => {
    setSelected(prev => {
      const next = {};
      Object.keys(prev).forEach(id => { next[id] = accountTier; });
      return next;
    });
  }, [accountTier]);

  const toggleService = (id) => setSelected(prev => {
    const next = { ...prev };
    if (next[id]) delete next[id]; else next[id] = accountTier;
    return next;
  });

  const daily = Object.keys(selected).reduce((sum, id) => sum + (services[id]?.pricing?.[accountTier]?.hourly_rate || 0) * hoursPerDay, 0);
  const monthly = daily * 30;
  const netAfterCredit = monthly - wcAmount;
  const suggestedTopup = !user && wcAmount > 0 ? Math.max(0, Math.ceil(netAfterCredit / 10) * 10) : Math.max(10, Math.ceil(monthly / 10) * 10);

  const handleTopup = async (amount) => {
    if (!user) { navigate('/signup'); return; }
    setLoading('topup');
    try { const res = await walletApi.topup({ amount, origin_url: window.location.origin }); if (res.data.url) window.location.href = res.data.url; } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setLoading('');
  };

  // Group services by category
  const grouped = {};
  Object.entries(services).forEach(([id, svc]) => {
    if (!grouped[svc.category]) grouped[svc.category] = [];
    grouped[svc.category].push({ ...svc, id });
  });

  const filteredServices = activeCategory === 'all'
    ? Object.entries(services).map(([id, svc]) => ({ ...svc, id }))
    : (grouped[activeCategory] || []);

  if (!content || !pricingData) return <div className="min-h-screen bg-white flex items-center justify-center"><div className="w-8 h-8 border-2 brand-spinner rounded-full animate-spin" /></div>;

  return (
    <div data-testid="pricing-page" className="light-page min-h-screen bg-white text-slate-900">
      <PublicNav />
      <AnalyticsTracker sections={['cost-calculator']} />

      {/* Hero */}
      <section className="pt-28 pb-8 md:pt-36 md:pb-10 px-5 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/3 w-[500px] h-[500px] brand-mesh-1 rounded-full blur-[120px] opacity-50" />
          <div className="absolute top-20 right-1/4 w-[400px] h-[400px] brand-mesh-2 rounded-full blur-[100px] opacity-40" />
        </div>
        <div className="max-w-3xl mx-auto text-center relative">
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 mb-4" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>
            {pricingSection.title || 'Pay Only for What You Use'}
          </h1>
          <p className="text-lg text-slate-500 max-w-xl mx-auto">{pricingSection.subtitle || 'No subscriptions. No commitments. Start with just $10.'}</p>
        </div>
      </section>

      {/* $10 Banner */}
      <section className="pb-10 px-5">
        <div className="max-w-3xl mx-auto">
          <div className="relative overflow-hidden rounded-2xl brand-gradient-bg p-7 text-center text-white shadow-xl">
            <div className="absolute inset-0 opacity-20"><div className="absolute top-0 right-0 w-60 h-60 bg-white rounded-full blur-[80px]" /></div>
            <div className="relative">
              <p className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-1.5">{pricingBanner.badge || 'Lowest barrier to entry'}</p>
              <h2 className="text-3xl md:text-4xl font-extrabold mb-2" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>{pricingBanner.title || 'Start with just $10'}</h2>
              <p className="text-white/70 text-sm mb-4">{pricingBanner.examples || '500 hours of spaCy NLP \u00b7 100 hours of BGE-M3 \u00b7 166 hours of Qdrant'}</p>
              <button data-testid="start-10-cta" onClick={() => navigate('/signup')}
                className="brand-btn inline-flex items-center gap-2 px-7 py-3 text-sm">
                {loading === 'topup' ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Create Free Account {wcAmount > 0 ? `— $${wcAmount} Credit Included` : ''} <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator */}
      <section data-testid="cost-calculator" className="py-12 px-5 bg-slate-50 border-y border-slate-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-extrabold text-slate-900 flex items-center justify-center gap-2" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>
              <Calculator className="w-5 h-5 brand-text" /> Build Your Estimate
            </h2>
            <p className="text-slate-500 text-sm mt-1">Pick a tier, then check the services you need. One tier applies to all selected services.</p>
          </div>

          {/* Phase 8 — Account Tier selector (all 3 selectable; no plan lock) */}
          <div className="max-w-2xl mx-auto mb-6" data-testid="pricing-tier-selector">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 text-center">Step 1 — Pick your tier</p>
            <div className="grid grid-cols-3 gap-2">
              {['economy', 'standard', 'priority'].map((t) => {
                const ts = tierStyles[t];
                const active = accountTier === t;
                return (
                  <button
                    key={t}
                    data-testid={`pricing-tier-option-${t}`}
                    onClick={() => setAccountTier(t)}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${active ? ts.active : `${ts.bg} ${ts.border} ${ts.text} hover:opacity-90`}`}
                  >
                    <p className="text-sm font-extrabold uppercase tracking-wider">{ts.label}</p>
                    <p className="text-[10px] opacity-80 mt-0.5">{ts.sub}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Category filters */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
            <button onClick={() => setActiveCategory('all')} className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all ${activeCategory === 'all' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}>
              All Services
            </button>
            {Object.entries(catMeta).map(([key, meta]) => (
              <button key={key} onClick={() => setActiveCategory(key)} className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all flex items-center gap-1.5 ${activeCategory === key ? 'brand-btn-solid border-transparent' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                <meta.icon className="w-3 h-3" />{meta.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Service cards */}
            <div className="lg:col-span-2 space-y-3">
              {filteredServices.map(svc => {
                const Icon = svcIcons[svc.id] || Cpu;
                const isSelected = !!selected[svc.id];
                const p = svc.pricing?.[accountTier];
                const price = p?.hourly_rate || 0;
                const latency = p?.max_latency_seconds || p?.timeout_seconds || 10;
                const rpd = p?.max_requests_per_day || 0;
                const ts = tierStyles[accountTier];
                return (
                  <div key={svc.id} data-testid={`calc-service-${svc.id}`}
                    className={`rounded-2xl border p-5 transition-all ${isSelected ? 'brand-border brand-bg-muted brand-shadow' : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'}`}>
                    {/* Header row */}
                    <div className="flex items-center gap-3 mb-1">
                      <input
                        type="checkbox"
                        data-testid={`calc-service-checkbox-${svc.id}`}
                        checked={isSelected}
                        onChange={() => toggleService(svc.id)}
                        className="w-4 h-4 rounded border-slate-300 brand-accent-input shrink-0"
                      />
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isSelected ? 'brand-bg-soft' : 'bg-slate-100'}`}>
                        <Icon className={`w-4 h-4 ${isSelected ? 'brand-text' : 'text-slate-500'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-slate-900">{svc.name}</h3>
                        <p className="text-xs text-slate-500 truncate">{svc.friendly_description || svc.description}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-lg font-extrabold font-mono text-slate-900" data-testid={`calc-service-rate-${svc.id}`}>
                          ${price}<span className="text-[10px] font-normal opacity-70">/hr</span>
                        </p>
                        <p className="text-[10px] uppercase tracking-wider text-slate-500">{ts?.label}</p>
                      </div>
                    </div>
                    {/* Tier details for selected tier only */}
                    <div className="grid grid-cols-2 gap-2 mt-2 ml-11">
                      <p className="text-[11px] flex items-center gap-1 text-slate-500">
                        <Clock className="w-3 h-3" /> {'\u2264'} {latency}s response
                      </p>
                      <p className="text-[11px] flex items-center gap-1 text-slate-500">
                        <BarChart3 className="w-3 h-3" /> {rpd > 0 ? (rpd >= 1000 ? `${(rpd/1000).toLocaleString()}K` : rpd.toLocaleString()) : 'Unlimited'} req/day
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Sticky estimate */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 rounded-2xl brand-border bg-white p-5 shadow-lg brand-shadow">
                <h3 className="text-base font-bold text-slate-900 mb-3" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>Your Estimate</h3>
                {Object.keys(selected).length === 0 ? (
                  <p className="text-sm text-slate-400 py-6 text-center">Select services to see pricing</p>
                ) : (
                  <div className="space-y-3">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">
                      {Object.keys(selected).length} service{Object.keys(selected).length > 1 ? 's' : ''} selected · tier{' '}
                      <span className={`font-bold ${tierStyles[accountTier]?.text || ''}`} data-testid="estimate-tier-label">
                        {tierStyles[accountTier]?.label}
                      </span>
                    </p>
                    {Object.keys(selected).map((id) => {
                      const svc = services[id];
                      const p = svc?.pricing?.[accountTier];
                      const rate = p?.hourly_rate || 0;
                      const latency = p?.max_latency_seconds || p?.timeout_seconds || 10;
                      const rpd = p?.max_requests_per_day || 0;
                      const ts = tierStyles[accountTier] || {};
                      return (
                        <div key={id} className="p-3 rounded-xl bg-slate-50 border border-slate-100 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-bold text-slate-900 truncate mr-2">{svc?.name}</span>
                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${ts.bg || 'bg-slate-100'} ${ts.text || 'text-slate-600'} border ${ts.border || 'border-slate-200'}`}>{ts.label || accountTier}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs text-slate-500">
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{'\u2264'}{latency}s</span>
                            <span className="flex items-center gap-1"><BarChart3 className="w-3 h-3" />{rpd > 0 ? (rpd >= 1000 ? `${(rpd/1000)}K` : rpd) : '\u221e'} req/day</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-slate-500">${rate}/hr x {hoursPerDay}h</span>
                            <span className="text-sm font-bold font-mono text-slate-900">${(rate * hoursPerDay).toFixed(2)}/day</span>
                          </div>
                        </div>
                      );
                    })}
                    <div className="pt-2 space-y-1">
                      <label className="text-[10px] text-slate-500 uppercase tracking-wider">Hours per day</label>
                      <div className="flex items-center gap-2">
                        <input type="range" min="1" max="24" value={hoursPerDay} onChange={e => setHoursPerDay(parseInt(e.target.value))}
                          className="flex-1 h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer brand-accent-input" />
                        <span className="text-sm font-bold font-mono brand-text w-8 text-center">{hoursPerDay}h</span>
                      </div>
                    </div>
                    <div className="border-t border-slate-200 pt-3 mt-2 space-y-1">
                      <div className="flex justify-between text-sm"><span className="text-slate-500">Daily</span><span className="font-mono font-bold">${daily.toFixed(2)}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-slate-500">Monthly (30 days)</span><span className="font-mono font-bold">${monthly.toFixed(2)}</span></div>
                      {wcAmount > 0 && (
                        <>
                          <div className="flex justify-between text-sm text-emerald-600">
                            <span className="flex items-center gap-1">Free credit {!user ? '(new account)' : ''}</span>
                            <span className="font-mono font-bold">-${wcAmount.toFixed(2)}</span>
                          </div>
                          <div className="border-t border-slate-200 pt-2 mt-1">
                            <div className="flex justify-between text-lg font-extrabold">
                              <span className="text-slate-900">{!user ? 'You pay today' : 'After credit'}</span>
                              <span className={`font-mono ${netAfterCredit <= 0 ? 'text-emerald-600' : 'brand-text'}`}>
                                {netAfterCredit <= 0 ? '$0.00' : `$${netAfterCredit.toFixed(2)}`}
                              </span>
                            </div>
                            {netAfterCredit <= 0 && (
                              <p className="text-xs text-emerald-600 mt-1">
                                Your ${wcAmount} free credit covers {monthly > 0 ? Math.floor(wcAmount / monthly) : '\u221e'} month{monthly > 0 && Math.floor(wcAmount / monthly) !== 1 ? 's' : ''} at this usage level
                              </p>
                            )}
                          </div>
                        </>
                      )}
                      {!wcAmount && (
                        <div className="flex justify-between text-lg font-extrabold brand-text"><span>Monthly</span><span className="font-mono">${monthly.toFixed(2)}</span></div>
                      )}
                    </div>
                    <button data-testid="calc-topup-cta" onClick={() => user ? handleTopup(suggestedTopup) : navigate('/signup')}
                      className="w-full py-3 mt-2 brand-btn text-sm">
                      {loading === 'topup' ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (
                        !user && wcAmount > 0 && netAfterCredit <= 0
                          ? 'Create Free Account \u2014 $0 Due Today'
                          : !user && wcAmount > 0
                            ? `Create Account \u2014 Only $${netAfterCredit.toFixed(2)} After Free Credit`
                            : `Add $${suggestedTopup} & Start`
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rate table */}
      <section className="py-14 px-5">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-extrabold text-center text-slate-900 mb-6" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>Per-Service Hourly Rates</h2>
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 border-b border-slate-200"><th className="text-left py-3 px-5 font-bold text-slate-700 text-xs uppercase">Service</th><th className="text-right py-3 px-5 font-bold text-slate-400 text-xs uppercase">Economy</th><th className="text-right py-3 px-5 font-bold text-cyan-600 text-xs uppercase">Standard</th><th className="text-right py-3 px-5 font-bold text-amber-600 text-xs uppercase">Priority</th></tr></thead>
              <tbody>
                {Object.entries(services).map(([id, svc], i) => (
                  <tr key={id} className={`border-b border-slate-100 last:border-0 ${i % 2 ? 'bg-slate-50/30' : ''}`}>
                    <td className="py-3 px-5"><p className="font-semibold text-slate-900">{svc.name}</p><p className="text-[11px] text-slate-400">{svc.friendly_description || svc.description}</p></td>
                    {['economy', 'standard', 'priority'].map(t => { const p = svc.pricing?.[t]; const rpd = p?.max_requests_per_day; return <td key={t} className="py-3 px-5 text-right"><span className="font-mono font-bold text-slate-900">${p?.hourly_rate}</span><span className="text-slate-400 font-normal">/hr</span><span className="block text-[10px] text-slate-400">{'\u2264'}{p?.max_latency_seconds || p?.timeout_seconds}s {rpd && rpd > 0 ? `| ${rpd >= 1000 ? `${rpd/1000}K` : rpd}/day` : ''}</span></td>; })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 px-5 bg-slate-50 border-t border-slate-100">
        <div className="max-w-3xl mx-auto space-y-3">
          {(pricingFaq.length > 0 ? pricingFaq : [
            { q: 'How does billing work?', a: 'Add credit to your wallet, enable services, make API calls. Usage is calculated hourly and deducted from your wallet automatically.' },
            { q: 'What happens when my balance runs out?', a: "API calls are blocked with a 402 error. You'll receive an email alert when your balance is low. Add credit to resume instantly." },
            { q: 'Can I set up auto top-up?', a: 'Yes. Save a payment method in your dashboard and set a threshold. When your balance drops below it, your card is charged automatically.' },
            { q: "What's the minimum top-up?", a: "Just $10. That's enough for hundreds of hours of basic services." },
          ]).map((faq, i) => (
            <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-white border border-slate-100">
              <HelpCircle className="w-4 h-4 brand-text mt-0.5 shrink-0" />
              <div><p className="font-bold text-sm text-slate-900">{faq.q}</p><p className="text-xs text-slate-600 mt-0.5">{faq.a}</p></div>
            </div>
          ))}
        </div>
      </section>

      <PublicFooter footer={footer} />
    </div>
  );
}
